import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams, NavLink } from "react-router-dom";
import {
  Plus, Check, MoreHorizontal, ArrowUp, ArrowDown, Trash2, Clock, CalendarDays,
  Play, RotateCcw, ChevronRight, PenLine,
} from "lucide-react";
import MOShell, {
  useMOAccess, isWritable, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import {
  listProductionTasks, createProductionTask, updateProductionTask,
  deleteProductionTask, reorderProductionTasks,
  listOrders, listOrderItems, listProductTypes,
  PRODUCTION_STATUSES, productionStatusLabel,
  suggestProductionPlan, listUnplannedOrders,
  createProductionScenario, displayOrderNumber,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";
import ApplyScenarioModal from "@/components/masterOrder/ApplyScenarioModal";

/* --- Plan review drawer (system-suggested draft, editable before save) --- */
function PlanReviewDrawer({ plan, onChange, onClose, onSave, onSaveAsScenario, canWrite }) {
  const stages = plan.stages;
  const setStages = (next) => onChange({ ...plan, stages: next.map((s, i) => ({ ...s, sort_order: i })) });
  const patch = (i, p) => setStages(stages.map((s, k) => (k === i ? { ...s, ...p } : s)));
  const remove = (i) => setStages(stages.filter((_, k) => k !== i));
  const move = (i, dir) => {
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= stages.length) return;
    const n = [...stages]; [n[i], n[j]] = [n[j], n[i]]; setStages(n);
  };
  const add = () => setStages([...stages, { title: "", scheduled_date: stages[stages.length - 1]?.scheduled_date || todayISO(), start_time: null, sort_order: stages.length }]);

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
         data-testid="mo-plan-review">
      <div className="w-full md:max-w-xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl p-5 md:p-6 max-h-[92vh] overflow-y-auto"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)" }}>
        <div className="text-[19px] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          Производственный план
        </div>
        <div className="text-[12px] mb-4" style={{ color: "#8B6F4B" }} data-testid="mo-plan-review-lede">
          Мы подготовили план по этому заказу. Проверьте даты и этапы и при необходимости измените их.
        </div>
        <div className="space-y-2" data-testid="mo-plan-review-stages">
          {stages.map((s, i) => (
            <div key={i} className="rounded-lg bg-white px-3 py-2.5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-plan-review-stage">
              <div className="flex items-center gap-2 mb-1.5">
                <input value={s.title} onChange={(e) => patch(i, { title: e.target.value })}
                       placeholder="Название этапа"
                       className="flex-1 h-8 px-2 rounded-md bg-white text-[12.5px] outline-none"
                       style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                       data-testid="mo-plan-review-title" />
                <button onClick={() => move(i, "up")} disabled={i === 0} data-testid="mo-plan-review-up"
                        className="w-7 h-7 rounded-full grid place-items-center disabled:opacity-30"
                        style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#4A3728" }} aria-label="Выше">
                  <ArrowUp size={12} />
                </button>
                <button onClick={() => move(i, "down")} disabled={i === stages.length - 1} data-testid="mo-plan-review-down"
                        className="w-7 h-7 rounded-full grid place-items-center disabled:opacity-30"
                        style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#4A3728" }} aria-label="Ниже">
                  <ArrowDown size={12} />
                </button>
                <button onClick={() => remove(i)} data-testid="mo-plan-review-remove"
                        className="w-7 h-7 rounded-full grid place-items-center"
                        style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#8B4B2E" }} aria-label="Удалить">
                  <Trash2 size={12} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <input type="date" value={s.scheduled_date || ""}
                       onChange={(e) => patch(i, { scheduled_date: e.target.value })}
                       className="h-8 px-2 rounded-md bg-white text-[12px] outline-none"
                       style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                       data-testid="mo-plan-review-date" />
                <input type="time" value={s.start_time ? s.start_time.slice(0, 5) : ""}
                       onChange={(e) => patch(i, { start_time: e.target.value || null })}
                       placeholder="время"
                       className="h-8 px-2 rounded-md bg-white text-[12px] outline-none"
                       style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                       data-testid="mo-plan-review-time" />
              </div>
            </div>
          ))}
        </div>
        <button onClick={add} className="mt-2 h-9 w-full rounded-lg text-[12.5px] inline-flex items-center justify-center gap-1.5"
                style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid="mo-plan-review-add">
          <Plus size={13} /> Добавить этап
        </button>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {canWrite && stages.length > 0 && (
            <button onClick={onSaveAsScenario}
                    className="h-10 px-3 rounded-full text-[12.5px]"
                    style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-plan-review-save-scenario">
              Сохранить как мой сценарий
            </button>
          )}
          <button onClick={onClose}
                  className="h-10 px-4 rounded-full text-[13px]"
                  style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-plan-review-cancel">
            Отмена
          </button>
          {canWrite && (
            <button onClick={onSave}
                    className="h-10 px-5 rounded-full text-[13px]"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-plan-review-save">
              Сохранить план
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------- Production tabs (План / Мои производственные сценарии) -------- */
function ProductionTabs() {
  const link = (to, label, testId) => (
    <NavLink to={to} end
             className="h-9 px-3.5 rounded-full text-[12.5px] flex items-center transition"
             style={({ isActive }) => ({
               background: isActive ? "#4A3728" : "#F7F1E7",
               color: isActive ? "#F7EFE0" : "#4A3728",
               boxShadow: isActive ? "none" : "inset 0 0 0 1px #EFE4D0",
             })}
             data-testid={testId}>
      {label}
    </NavLink>
  );
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="mo-production-tabs">
      {link("/master-order/production", "План", "mo-tab-plan")}
      {link("/master-order/production/scenarios",
            "Мои производственные сценарии", "mo-tab-scenarios")}
    </div>
  );
}

/* ---------------------------- date helpers ------------------------------ */

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const addDaysISO = (isoDate, days) => {
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const RU_MONTHS_FULL = [
  "января","февраля","марта","апреля","мая","июня",
  "июля","августа","сентября","октября","ноября","декабря",
];
const RU_WEEKDAYS = ["воскресенье","понедельник","вторник","среда","четверг","пятница","суббота"];
const fmtLongDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${RU_MONTHS_FULL[d.getMonth()]}`;
};
const fmtWeekday = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return RU_WEEKDAYS[d.getDay()];
};
const relativeLabel = (iso) => {
  if (iso === todayISO()) return "Сегодня";
  if (iso === addDaysISO(todayISO(), 1)) return "Завтра";
  return null;
};
const fmtTime = (t) => (t ? t.slice(0, 5) : null); // "08:30:00" -> "08:30"
const fmtDuration = (mins) => {
  if (!mins) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h} ч ${m} мин`;
  if (h) return `${h} ч`;
  return `${m} мин`;
};

/* ------------------------------ presets --------------------------------- */

const PRESETS = [
  { id: "today",  label: "Сегодня",  days: 0 },
  { id: "tomorrow", label: "Завтра", days: 1, offsetStart: 1 },
  { id: "7d",     label: "7 дней",   days: 6 },
  { id: "custom", label: "Свой период", days: null },
];

function PeriodPicker({ preset, onPreset, start, end, onCustom, total }) {
  return (
    <div className="rounded-2xl bg-white px-4 py-3 md:px-5 md:py-4"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-production-period">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => {
          const active = preset === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onPreset(p.id)}
              className="h-9 px-3.5 rounded-full text-[12.5px] transition"
              style={{
                background: active ? "#4A3728" : "#F7F1E7",
                color: active ? "#F7EFE0" : "#4A3728",
                boxShadow: active ? "none" : "inset 0 0 0 1px #EFE4D0",
              }}
              data-testid={`mo-production-preset-${p.id}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {preset === "custom" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[12.5px]" style={{ color: "#4A3728" }}>
          <label className="flex items-center gap-1.5">
            <CalendarDays size={13} color="#8B6F4B" />
            <span>С</span>
            <input type="date" value={start} onChange={(e) => onCustom(e.target.value, end)}
                   className="h-9 px-2 rounded-lg bg-white outline-none text-[12.5px]"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-production-custom-start" />
          </label>
          <label className="flex items-center gap-1.5">
            <span>по</span>
            <input type="date" value={end} onChange={(e) => onCustom(start, e.target.value)}
                   className="h-9 px-2 rounded-lg bg-white outline-none text-[12.5px]"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-production-custom-end" />
          </label>
        </div>
      )}
      <div className="mt-3 text-[12.5px]" style={{ color: "#8B6F4B" }} data-testid="mo-production-summary">
        {total === 0 ? "задач нет" : `${total} ${total === 1 ? "задача" : total < 5 ? "задачи" : "задач"}`}
      </div>
    </div>
  );
}

/* ------------------------------- task card ------------------------------ */

function StatusPill({ status }) {
  const label = productionStatusLabel(status);
  const style =
    status === "done"        ? { bg: "#E8DFC9", fg: "#5A4A2A" } :
    status === "in_progress" ? { bg: "#F5EBD0", fg: "#7A5A16" } :
                               { bg: "#FDF3D9", fg: "#5A3E10" };
  return (
    <span className="inline-flex items-center h-6 px-2 rounded-full text-[10.5px]"
          style={{ background: style.bg, color: style.fg }}
          data-testid="mo-production-task-status-label">
      {label}
    </span>
  );
}

function TaskCard({ task, orderMap, itemMap, ptMap, canWrite, onEdit, onDelete, onStatusCycle, onMoveUp, onMoveDown, first, last }) {
  const order = orderMap[task.order_id];
  const orderLabel = order ? `Заказ № ${displayOrderNumber(order.order_number)}` : "Заказ";
  const item = task.order_item_id ? itemMap[task.order_item_id] : null;
  const ptName = item ? (ptMap[item.product_type_id]?.name || "Позиция") : null;
  const itemLabel = task.order_item_id ? (ptName || "Позиция") : "Весь заказ";
  const time = fmtTime(task.start_time);
  const dur = fmtDuration(task.duration_minutes);
  const done = task.status === "done";

  return (
    <div className="rounded-2xl bg-white px-4 py-3.5 md:px-5 md:py-4"
         style={{
           boxShadow: done ? "inset 0 0 0 1px #E4D9C4" : "inset 0 0 0 1px #EFE4D0",
           opacity: done ? 0.72 : 1,
         }}
         data-testid="mo-production-task">
      <div className="flex items-start gap-3">
        {/* Status toggle circle */}
        <button
          onClick={() => canWrite && onStatusCycle(task)}
          disabled={!canWrite}
          className="w-9 h-9 rounded-full shrink-0 grid place-items-center transition"
          style={{
            background: done ? "#4A3728" : "#F7F1E7",
            color: done ? "#F7EFE0" : "#8B6F4B",
            boxShadow: done ? "none" : "inset 0 0 0 1px #EFE4D0",
            cursor: canWrite ? "pointer" : "default",
          }}
          title={done ? "Готово" : task.status === "in_progress" ? "В работе — отметить готовым" : "Начать"}
          data-testid="mo-production-task-toggle"
        >
          {done ? <Check size={15} strokeWidth={2.2} /> :
           task.status === "in_progress" ? <Play size={13} strokeWidth={2} /> :
           <div className="w-3.5 h-3.5 rounded-full" style={{ boxShadow: "inset 0 0 0 1px #C7A574" }} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="text-[14.5px] md:text-[15px] leading-snug"
                 style={{
                   color: "#2A1D14",
                   fontFamily: "'Cormorant Garamond', serif",
                   textDecoration: done ? "line-through" : "none",
                   textDecorationColor: "#B9A692",
                 }}
                 data-testid="mo-production-task-title">
              {task.title}
            </div>
            <StatusPill status={task.status} />
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px]"
               style={{ color: "#8B6F4B" }} data-testid="mo-production-task-meta">
            <span style={{ color: "#4A3728" }}>{orderLabel}</span>
            <span>·</span>
            <span data-testid="mo-production-task-scope">{itemLabel}</span>
            {(time || dur) && <span>·</span>}
            {time && <span className="inline-flex items-center gap-0.5"><Clock size={11} color="#8B6F4B" />{time}</span>}
            {dur && <span>· {dur}</span>}
          </div>

          {task.notes && (
            <div className="mt-1.5 text-[12px] leading-relaxed" style={{ color: "#5B4326" }}
                 data-testid="mo-production-task-notes">
              {task.notes}
            </div>
          )}
        </div>

        {canWrite && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => onMoveUp(task)} disabled={first}
                    className="w-8 h-8 rounded-full grid place-items-center disabled:opacity-30"
                    style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#4A3728" }}
                    title="Выше" aria-label="Выше"
                    data-testid="mo-production-task-up">
              <ArrowUp size={13} />
            </button>
            <button onClick={() => onMoveDown(task)} disabled={last}
                    className="w-8 h-8 rounded-full grid place-items-center disabled:opacity-30"
                    style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#4A3728" }}
                    title="Ниже" aria-label="Ниже"
                    data-testid="mo-production-task-down">
              <ArrowDown size={13} />
            </button>
            <button onClick={() => onEdit(task)}
                    className="w-8 h-8 rounded-full grid place-items-center"
                    style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#4A3728" }}
                    title="Редактировать" aria-label="Редактировать"
                    data-testid="mo-production-task-edit">
              <PenLine size={13} />
            </button>
            <button onClick={() => onDelete(task)}
                    className="w-8 h-8 rounded-full grid place-items-center"
                    style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#8B4B2E" }}
                    title="Удалить" aria-label="Удалить"
                    data-testid="mo-production-task-delete">
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------- create/edit modal -------------------------- */

function TaskEditor({ mode, initial, prefillOrderId, onClose, onSubmit, orders, itemsCache, loadItems, ptMap }) {
  const [orderId, setOrderId] = useState(initial?.order_id || prefillOrderId || (orders[0]?.id || ""));
  const [scope, setScope] = useState(initial?.order_item_id ? "item" : "order");
  const [orderItemId, setOrderItemId] = useState(initial?.order_item_id || "");
  const [title, setTitle] = useState(initial?.title || "");
  const [scheduledDate, setScheduledDate] = useState(initial?.scheduled_date || todayISO());
  const [startTime, setStartTime] = useState(initial?.start_time ? initial.start_time.slice(0, 5) : "");
  const [duration, setDuration] = useState(initial?.duration_minutes ? String(initial.duration_minutes) : "");
  const [status, setStatus] = useState(initial?.status || "planned");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [saving, setSaving] = useState(false);
  const items = itemsCache[orderId] || [];

  useEffect(() => {
    if (orderId) loadItems(orderId);
  }, [orderId, loadItems]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Название задачи не может быть пустым"); return; }
    if (!scheduledDate)  { toast.error("Укажите дату"); return; }
    if (!orderId)       { toast.error("Выберите заказ"); return; }
    const durNum = duration ? Number(duration) : null;
    if (durNum !== null && (!Number.isFinite(durNum) || durNum <= 0)) {
      toast.error("Длительность должна быть больше 0"); return;
    }
    setSaving(true);
    try {
      if (mode === "create") {
        const payload = {
          order_id: orderId,
          order_item_id: scope === "item" ? orderItemId : null,
          title: title.trim(),
          scheduled_date: scheduledDate,
          start_time: startTime ? `${startTime}:00` : null,
          duration_minutes: durNum,
          notes: notes.trim() || null,
        };
        await onSubmit(payload);
      } else {
        const payload = {
          title: title.trim(),
          scheduled_date: scheduledDate,
          start_time: startTime ? `${startTime}:00` : null,
          duration_minutes: durNum,
          status,
          notes: notes.trim() || null,
        };
        await onSubmit(payload);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="mo-production-editor">
      <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl p-5 md:p-6 max-h-[92vh] overflow-y-auto"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)" }}>
        <div className="text-[19px] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          {mode === "create" ? "Новый этап производства" : "Изменить этап"}
        </div>

        <div className="space-y-3">
          <div>
            <Label>Заказ</Label>
            <select value={orderId} disabled={mode === "edit"}
                    onChange={(e) => { setOrderId(e.target.value); setOrderItemId(""); }}
                    className="w-full h-10 px-3 rounded-lg bg-white text-[13px] outline-none disabled:opacity-70"
                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                    data-testid="mo-production-editor-order">
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  № {displayOrderNumber(o.order_number)} · {o.event_date}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label>Что нужно сделать</Label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Например: Выпечь бисквиты"
                   className="w-full h-10 px-3 rounded-lg bg-white text-[13px] outline-none"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-production-editor-title" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Дата</Label>
              <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)}
                     className="w-full h-10 px-3 rounded-lg bg-white text-[13px] outline-none"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-production-editor-date" />
            </div>
            <div>
              <Label>Время</Label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                     placeholder="—"
                     className="w-full h-10 px-3 rounded-lg bg-white text-[13px] outline-none"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-production-editor-time" />
            </div>
            <div>
              <Label>Длит., мин</Label>
              <input type="number" min={1} value={duration} onChange={(e) => setDuration(e.target.value)}
                     placeholder="—"
                     className="w-full h-10 px-3 rounded-lg bg-white text-[13px] outline-none tabular-nums"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-production-editor-duration" />
            </div>
          </div>

          {mode === "edit" && (
            <div>
              <Label>Статус</Label>
              <div className="flex flex-wrap gap-2">
                {PRODUCTION_STATUSES.map((s) => (
                  <ChipRadio key={s.value} active={status === s.value} onClick={() => setStatus(s.value)}
                             testId={`mo-production-editor-status-${s.value}`}>
                    {s.label}
                  </ChipRadio>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Заметка</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Необязательно"
                      className="w-full px-3 py-2 rounded-lg bg-white text-[13px] outline-none resize-none"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-production-editor-notes" />
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-full text-[13px]"
                  style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-production-editor-cancel">
            Отмена
          </button>
          <button onClick={handleSave} disabled={saving}
                  className="h-10 px-5 rounded-full text-[13px]"
                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                  data-testid="mo-production-editor-save">
            {saving ? "Сохраняем..." : mode === "create" ? "Добавить" : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>{children}</div>;
}
function ChipRadio({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick}
            className="h-9 px-3.5 rounded-full text-[12.5px] transition"
            style={{
              background: active ? "#4A3728" : "#F7F1E7",
              color: active ? "#F7EFE0" : "#4A3728",
              boxShadow: active ? "none" : "inset 0 0 0 1px #EFE4D0",
            }}
            data-testid={testId}>
      {children}
    </button>
  );
}

/* ------------------------------ status cycle ---------------------------- */

const NEXT_STATUS = { planned: "in_progress", in_progress: "done", done: "planned" };

/* --------------------------------- page --------------------------------- */

export default function MOProduction() {
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const focusedOrderId = searchParams.get("order_id");
  const openNewOnMount = searchParams.get("new") === "1";

  const [preset, setPreset] = useState("today");
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [tasks, setTasks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [itemsCache, setItemsCache] = useState({}); // orderId -> items[]
  const [loading, setLoading] = useState(true);
  const [editorMode, setEditorMode] = useState(null); // null | "create" | "edit"
  const [editing, setEditing] = useState(null);
  const [showChooser, setShowChooser] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [unplanned, setUnplanned] = useState([]);
  const [planReview, setPlanReview] = useState(null); // {order_id, stages: [{title,scheduled_date,start_time,sort_order}], product_type_name}

  const orderMap = useMemo(() => Object.fromEntries(orders.map((o) => [o.id, o])), [orders]);
  const ptMap = useMemo(() => Object.fromEntries(productTypes.map((p) => [p.id, p])), [productTypes]);
  const itemMap = useMemo(() => {
    const m = {};
    for (const list of Object.values(itemsCache)) for (const it of list) m[it.id] = it;
    return m;
  }, [itemsCache]);

  const applyPreset = (id) => {
    setPreset(id);
    const t = todayISO();
    const p = PRESETS.find((x) => x.id === id);
    if (!p || p.days === null) return;
    const s = p.offsetStart ? addDaysISO(t, p.offsetStart) : t;
    setStart(s);
    setEnd(addDaysISO(s, p.days - (p.offsetStart || 0)));
  };
  const setCustom = (s, e) => { setStart(s); setEnd(e); setPreset("custom"); };

  const loadItems = useCallback(async (orderId) => {
    if (itemsCache[orderId]) return;
    try {
      const list = await listOrderItems(orderId);
      setItemsCache((prev) => ({ ...prev, [orderId]: list }));
    } catch (e) { /* silent — non-blocking */ }
  }, [itemsCache]);

  const load = useCallback(async () => {
    if (start > end) return;
    setLoading(true);
    try {
      const params = { date_from: start, date_to: end };
      if (focusedOrderId) params.order_id = focusedOrderId;
      const [tRes, oRes, ptRes, unRes] = await Promise.all([
        listProductionTasks(params),
        listOrders(),
        listProductTypes(true),
        listUnplannedOrders({ date_from: start, date_to: end }).catch(() => []),
      ]);
      setTasks(tRes);
      setOrders(oRes);
      setProductTypes(ptRes);
      setUnplanned(focusedOrderId ? unRes.filter(o => o.id === focusedOrderId) : unRes);
      // Preload items for orders referenced by tasks
      const orderIds = [...new Set(tRes.map((t) => t.order_id))];
      for (const oid of orderIds) await loadItems(oid);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [start, end, focusedOrderId, loadItems]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [start, end, focusedOrderId]);

  // Deep-link from Order Detail: /master-order/production?order_id=...&new=1
  useEffect(() => {
    if (openNewOnMount && orders.length > 0 && editorMode === null) {
      setEditorMode("create");
    }
    /* eslint-disable-next-line */
  }, [openNewOnMount, orders.length]);

  // group tasks by date
  const groups = useMemo(() => {
    const g = new Map();
    for (const t of tasks) {
      if (!g.has(t.scheduled_date)) g.set(t.scheduled_date, []);
      g.get(t.scheduled_date).push(t);
    }
    // Ensure inner ordering (server already sorts, but be defensive)
    for (const arr of g.values()) {
      arr.sort((a, b) => (a.sort_order - b.sort_order) || (a.created_at.localeCompare(b.created_at)));
    }
    return [...g.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [tasks]);

  const doSubmit = async (payload) => {
    try {
      if (editorMode === "create") {
        await createProductionTask(payload);
        toast.success("Этап добавлен");
      } else if (editorMode === "edit" && editing) {
        await updateProductionTask(editing.id, payload);
        toast.success("Этап сохранён");
      }
      setEditorMode(null);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cycleStatus = async (task) => {
    try {
      await updateProductionTask(task.id, { status: NEXT_STATUS[task.status] });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const askDelete = async (task) => {
    if (!window.confirm(`Удалить этап «${task.title}»?`)) return;
    try {
      await deleteProductionTask(task.id);
      toast.success("Этап удалён");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (task, direction) => {
    // Move within its own date group.
    const groupArr = groups.find(([d]) => d === task.scheduled_date)?.[1] || [];
    const idx = groupArr.findIndex((t) => t.id === task.id);
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= groupArr.length) return;
    const next = [...groupArr];
    [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
    const entries = next.map((t, i) => ({ id: t.id, sort_order: i }));
    try {
      await reorderProductionTasks(entries);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openCreate = () => {
    if (orders.length === 0) {
      toast.error("Сначала создайте хотя бы один заказ");
      return;
    }
    setShowChooser(true);
  };
  const openSuggest = async (orderId) => {
    try {
      const draft = await suggestProductionPlan({ order_id: orderId });
      setPlanReview(draft);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const savePlan = async () => {
    if (!planReview) return;
    const stages = planReview.stages.filter(s => (s.title || "").trim() && s.scheduled_date);
    if (stages.length === 0) { toast.error("План пуст"); return; }
    try {
      for (const [i, s] of stages.entries()) {
        await createProductionTask({
          order_id: planReview.order_id,
          order_item_id: planReview.order_item_id || null,
          title: s.title.trim(),
          scheduled_date: s.scheduled_date,
          start_time: s.start_time ? (s.start_time.length === 5 ? `${s.start_time}:00` : s.start_time) : null,
          sort_order: i,
        });
      }
      toast.success("Производственный план сохранён");
      setPlanReview(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveAsScenario = async () => {
    if (!planReview) return;
    const name = window.prompt("Название сценария:", planReview.product_type_name || "Мой сценарий");
    if (!name || !name.trim()) return;
    try {
      await createProductionScenario({
        name: name.trim(),
        stages: planReview.stages
          .filter(s => (s.title || "").trim())
          .map((s, i) => ({ title: s.title.trim(), sort_order: i })),
      });
      toast.success("Сохранено как мой сценарий");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openManual = () => { setShowChooser(false); setEditing(null); setEditorMode("create"); };
  const openApply  = () => { setShowChooser(false); setShowApply(true); };
  const openEdit = (t) => { setEditing(t); setEditorMode("edit"); };

  const body = (() => {
    if (aLoad) return <LoadingBlock label="Проверяем доступ..." />;
    if (mode === "no_access" || mode === "demo") return <NoAccessScreen />;

    return (
      <>
        <ProductionTabs />
        {focusedOrderId && (
          <div className="mb-3 flex items-center gap-2 rounded-2xl bg-white px-4 py-3"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-production-order-focus">
            <div className="text-[12.5px]" style={{ color: "#4A3728" }}>
              Показаны задачи только для одного заказа{orderMap[focusedOrderId]?.order_number ? ` № ${String(orderMap[focusedOrderId].order_number).replace(/^0+/, "") || "0"}` : ""}.
            </div>
            <button onClick={() => nav(`/master-order/orders/${focusedOrderId}`)}
                    className="ml-auto h-8 px-3 rounded-full text-[12px]"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-production-back-to-order">
              К заказу
            </button>
            <button onClick={() => nav("/master-order/production")}
                    className="h-8 px-3 rounded-full text-[12px]"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-production-clear-focus">
              Показать все
            </button>
          </div>
        )}

        <PeriodPicker preset={preset} onPreset={applyPreset}
                      start={start} end={end} onCustom={setCustom}
                      total={tasks.length} />

        {canWrite && (
          <div className="mt-3 flex justify-end">
            <button onClick={openCreate}
                    className="h-10 px-4 rounded-full text-[13px] inline-flex items-center gap-2"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-production-add">
              <Plus size={14} /> Добавить этап
            </button>
          </div>
        )}

        {loading ? (
          <div className="mt-4"><LoadingBlock label="Собираем план..." /></div>
        ) : (
          <>
            {unplanned.length > 0 && (
              <section className="mt-4 rounded-2xl bg-white p-4 md:p-5"
                       style={{ boxShadow: "inset 0 0 0 1px #E9D28F", background: "#FDF3D9" }}
                       data-testid="mo-production-unplanned">
                <div className="text-[13.5px]" style={{ color: "#5A3E10", fontFamily: "'Cormorant Garamond', serif" }}
                     data-testid="mo-production-unplanned-title">
                  {`Для ${unplanned.length === 1 ? "этого заказа" : "этих заказов"} ещё нужно составить производственный план`}
                </div>
                <div className="text-[11.5px] mt-1 mb-3" style={{ color: "#8A6B2B" }}>
                  Мы предложим последовательность этапов — Вы сможете отредактировать даты и стадии перед сохранением.
                </div>
                <ul className="space-y-2" data-testid="mo-production-unplanned-list">
                  {unplanned.map(o => (
                    <li key={o.id} className="rounded-xl bg-white px-3.5 py-3 flex items-center gap-3"
                        style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                        data-testid="mo-production-unplanned-row">
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px]" style={{ color: "#2A1D14" }}>Заказ № {displayOrderNumber(o.order_number)}</div>
                        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>
                          {o.event_date}{o.event_time ? ` · ${o.event_time.slice(0,5)}` : ""}
                        </div>
                      </div>
                      {canWrite && (
                        <button onClick={() => openSuggest(o.id)}
                                className="h-9 px-3 rounded-full text-[12px]"
                                style={{ background: "#4A3728", color: "#F7EFE0" }}
                                data-testid="mo-production-unplanned-plan">
                          Составить план
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
        {!loading && groups.length === 0 && unplanned.length === 0 ? (
          <div className="mt-4 rounded-2xl bg-white p-8 md:p-10 text-center"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-production-empty">
            <div className="text-[16px] md:text-[18px]"
                 style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                 data-testid="mo-production-empty-title">
              {preset === "today"
                ? "На сегодня производственных задач нет"
                : "На выбранный период задач пока нет"}
            </div>
            <div className="text-[12.5px] mt-1.5 mx-auto max-w-md" style={{ color: "#8B6F4B" }}>
              Добавьте этап — например «Выпечь бисквит» или «Собрать торт» — и он появится здесь.
            </div>
            {canWrite && (
              <button onClick={openCreate}
                      className="mt-4 h-10 px-4 rounded-full text-[13px] inline-flex items-center gap-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-production-empty-add">
                <Plus size={14} /> Добавить этап
              </button>
            )}
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            {groups.map(([date, list]) => (
              <div key={date} data-testid="mo-production-date-group">
                <div className="flex items-baseline justify-between mb-2 px-1">
                  <div className="flex items-baseline gap-2">
                    {relativeLabel(date) && (
                      <span className="text-[12px] px-2 py-0.5 rounded-full"
                            style={{ background: "#EADFCF", color: "#4A3728" }}
                            data-testid="mo-production-relative">{relativeLabel(date)}</span>
                    )}
                    <h2 className="text-[18px] md:text-[20px]"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                        data-testid="mo-production-date">
                      {fmtLongDate(date)}
                    </h2>
                    <span className="text-[11.5px]" style={{ color: "#8B6F4B" }}>{fmtWeekday(date)}</span>
                  </div>
                  <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>
                    {list.length} · {list.filter((t) => t.status === "done").length} готово
                  </div>
                </div>
                <div className="space-y-2">
                  {list.map((t, i) => (
                    <TaskCard key={t.id} task={t}
                              orderMap={orderMap} itemMap={itemMap} ptMap={ptMap}
                              canWrite={canWrite}
                              onEdit={openEdit}
                              onDelete={askDelete}
                              onStatusCycle={cycleStatus}
                              onMoveUp={(x) => move(x, "up")}
                              onMoveDown={(x) => move(x, "down")}
                              first={i === 0}
                              last={i === list.length - 1} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {editorMode && (
          <TaskEditor
            mode={editorMode}
            initial={editing}
            prefillOrderId={focusedOrderId}
            orders={orders}
            itemsCache={itemsCache}
            loadItems={loadItems}
            ptMap={ptMap}
            onClose={() => { setEditorMode(null); setEditing(null); }}
            onSubmit={doSubmit}
          />
        )}

        {showChooser && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
               style={{ background: "rgba(35,23,16,0.5)" }}
               onClick={(e) => { if (e.target === e.currentTarget) setShowChooser(false); }}
               data-testid="mo-production-chooser">
            <div className="w-full md:max-w-sm bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl p-5 md:p-6"
                 style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)" }}>
              <div className="text-[18px] mb-3"
                   style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                Как добавить этапы?
              </div>
              <button onClick={openManual}
                      className="w-full h-11 rounded-full text-[13px] flex items-center justify-center gap-2 mb-2"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-production-chooser-manual">
                <Plus size={13} /> Создать вручную
              </button>
              <button onClick={openApply}
                      className="w-full h-11 rounded-full text-[13px] flex items-center justify-center gap-2"
                      style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-production-chooser-scenario">
                Из моего сценария
              </button>
            </div>
          </div>
        )}

        {showApply && (
          <ApplyScenarioModal
            orders={orders}
            preselectedOrderId={focusedOrderId}
            onClose={() => setShowApply(false)}
            onApplied={() => load()}
            onGoCreateScenario={() => nav("/master-order/production/scenarios")}
          />
        )}

        {planReview && (
          <PlanReviewDrawer
            plan={planReview}
            onChange={setPlanReview}
            onClose={() => setPlanReview(null)}
            onSave={savePlan}
            onSaveAsScenario={saveAsScenario}
            canWrite={canWrite}
          />
        )}
      </>
    );
  })();

  return (
    <MOShell active="production" title="Производство"
             subtitle="Что нужно сделать для заказов, и когда">
      {body}
    </MOShell>
  );
}
